/* PDP · Timeline (sections/pdp-timeline.liquid)
   <pdp-timeline> fills its steps as the page scrolls: the step whose middle is nearest the middle
   of the screen, and every step before it, carry `is-active`; scrolling back up takes it off again.
   A click on a step fills up to it and brings it to the middle of the screen. The markers are
   dropped to the middle of each step's label + heading, which depends on the fonts, so it is measured.
   The section prints every step `is-active` (the page is complete with no script); this file takes
   that off when it starts. No dependencies; window.PDP is used where it is there and not needed. */
(function () {
  'use strict';

  if (window.__pdpTimeline) return;
  window.__pdpTimeline = true;

  if (!('customElements' in window) || customElements.get('pdp-timeline')) return;

  var ITEM = '.pdp-timeline__item';
  var NO_ACTIVATION = 'a, input, select, textarea, label, summary, [data-stop-activation]';
  var START_RATIO = 0.15; // how much of the timeline has to be on screen before the first step fills
  var COMPRESS_MS = 1000; // a jump over several steps finishes inside this, then the pace is put back
  var SCROLLEND = 'onscrollend' in window;

  var reduceQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function reducedMotion() {
    return !!(reduceQuery && reduceQuery.matches);
  }

  function now() {
    return window.performance && performance.now ? performance.now() : Date.now();
  }

  function clamp(n, min, max) {
    return Math.min(max, Math.max(min, n));
  }

  function debounce(fn, ms) {
    if (window.PDP && typeof window.PDP.debounce === 'function') return window.PDP.debounce(fn, ms);
    var timer = 0;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(fn, ms);
    };
  }

  /* "0.5s" or "500ms" -> seconds */
  function seconds(value, fallback) {
    var text = String(value || '').trim();
    var n = parseFloat(text);
    if (!isFinite(n) || n <= 0) return fallback;
    return /ms$/i.test(text) ? n / 1000 : n;
  }

  class PdpTimeline extends HTMLElement {
    connectedCallback() {
      this._teardown();
      this._items = Array.prototype.slice.call(this.querySelectorAll(ITEM));

      if (!this._items.length) {
        // Upgraded before its children were parsed: look once more on the next frame.
        if (this._retried) return;
        this._retried = true;
        var self = this;
        this._retryRaf = requestAnimationFrame(function () {
          self._retryRaf = 0;
          if (self.isConnected) self.connectedCallback();
        });
        return;
      }

      this._setup();
    }

    disconnectedCallback() {
      this._teardown();
    }

    _setup() {
      var self = this;

      this._active = -1;
      this._started = false;
      this._near = false;
      this._listening = false;
      this._suppressUntil = 0;
      this._suppressedAt = 0;
      this._raf = 0;
      this._restoreTimer = 0;

      // Take the printed state off without playing it backwards.
      this.classList.add('is-booting');
      this._items.forEach(function (item, i) {
        item.setAttribute('data-index', i);
        item.classList.remove('is-active', 'is-current');
        item.removeAttribute('aria-current');
      });
      void this.offsetHeight;
      this.classList.remove('is-booting');
      this.classList.add('is-ready');

      this._onScroll = function () {
        if (self._raf) return;
        self._raf = requestAnimationFrame(function () {
          self._raf = 0;
          self._update();
        });
      };
      this._onScrollEnd = function () {
        // A scroll that was already running ends just after the click: not ours.
        if (now() - self._suppressedAt > 100) self._suppressUntil = 0;
      };
      this._onUserScroll = function () {
        self._suppressUntil = 0;
      };
      this._onResize = debounce(function () {
        if (!self.isConnected) return;
        self._align();
        self._update();
      }, 200);
      this._onLoad = function () {
        self._align();
      };
      this._onClick = function (event) {
        self._click(event);
      };
      this._onBlockSelect = function (event) {
        var target = event.target;
        var item = target && target.closest ? target.closest(ITEM) : null;
        var index = item ? self._items.indexOf(item) : -1;
        if (index < 0) return;
        self._started = true;
        self._suppress(1200);
        self._setActiveUpTo(index);
      };

      this.addEventListener('click', this._onClick);
      this.addEventListener('shopify:block:select', this._onBlockSelect);
      window.addEventListener('resize', this._onResize);
      window.addEventListener('load', this._onLoad);

      this._align();
      if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
        document.fonts.ready.then(function () {
          if (self.isConnected && self._items) self._align();
        });
      }

      if ('IntersectionObserver' in window) {
        // Only measure while the timeline is on screen or about to be.
        this._observer = new IntersectionObserver(
          function (entries) {
            var entry = entries[entries.length - 1];
            self._near = !!(entry && entry.isIntersecting);
            if (self._near) {
              self._listen(true);
              self._onScroll();
            } else {
              self._listen(false);
            }
          },
          { rootMargin: '25% 0px 25% 0px' }
        );
        this._observer.observe(this);
      } else {
        this._near = true;
        this._listen(true);
        this._onScroll();
      }
    }

    _teardown() {
      if (this._retryRaf) cancelAnimationFrame(this._retryRaf);
      this._retryRaf = 0;
      if (!this._items || !this._onScroll) return;

      this._listen(false);
      if (this._observer) this._observer.disconnect();
      this._observer = null;
      if (this._raf) cancelAnimationFrame(this._raf);
      this._raf = 0;
      clearTimeout(this._restoreTimer);
      this.style.removeProperty('--_dur');

      this.removeEventListener('click', this._onClick);
      this.removeEventListener('shopify:block:select', this._onBlockSelect);
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('load', this._onLoad);

      this._onScroll = null;
      this._items = null;
    }

    _listen(on) {
      if (on === this._listening) return;
      this._listening = on;
      var method = on ? 'addEventListener' : 'removeEventListener';
      var passive = { passive: true };
      window[method]('scroll', this._onScroll, passive);
      window[method]('wheel', this._onUserScroll, passive);
      window[method]('touchstart', this._onUserScroll, passive);
      if (SCROLLEND) window[method]('scrollend', this._onScrollEnd, passive);
    }

    /* The marker of each step sits at the middle of its label + heading; the line of the step
       before is shortened by the same amount, so it still stops 4px above the marker. Both are
       custom properties the stylesheet turns into margins. Reads first, then writes. */
    _align() {
      var items = this._items;
      if (!items) return;

      var drops = items.map(function (item) {
        var header = item.querySelector('.pdp-timeline__header');
        var point = item.querySelector('.pdp-timeline__point');
        if (!header || !point) return 0;
        return Math.max(0, Math.round((header.offsetHeight - point.offsetHeight) / 2));
      });

      items.forEach(function (item, i) {
        var next = i + 1 < drops.length ? drops[i + 1] : 0;
        if (drops[i] > 0) item.style.setProperty('--_d', drops[i] + 'px');
        else item.style.removeProperty('--_d');
        if (next > 0) item.style.setProperty('--_next-d', next + 'px');
        else item.style.removeProperty('--_next-d');
      });
    }

    _shouldStart() {
      var rect = this.getBoundingClientRect();
      var height = window.innerHeight || document.documentElement.clientHeight;
      var visible = Math.min(rect.bottom, height) - Math.max(rect.top, 0);
      // A timeline several screens long can never show 15% of itself: half a screen is enough.
      return visible > 0 && visible >= Math.min(rect.height * START_RATIO, height * 0.5);
    }

    _update() {
      var items = this._items;
      if (!items || !this._near) return;
      if (now() < this._suppressUntil) return;

      if (!this._started) {
        if (!this._shouldStart()) return;
        this._started = true;
      }

      var middle = (window.innerHeight || document.documentElement.clientHeight) / 2;
      var best = 0;
      var bestDistance = Infinity;
      for (var i = 0; i < items.length; i++) {
        var rect = items[i].getBoundingClientRect();
        var distance = Math.abs(rect.top + rect.height / 2 - middle);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      }

      if (best !== this._active) this._setActiveUpTo(best);
    }

    _setActiveUpTo(index) {
      var items = this._items;
      if (!items) return;
      for (var i = 0; i < items.length; i++) {
        items[i].classList.toggle('is-active', i <= index);
        items[i].classList.toggle('is-current', i === index);
        if (i === index) items[i].setAttribute('aria-current', 'step');
        else items[i].removeAttribute('aria-current');
      }
      this._active = index;
    }

    _suppress(ms) {
      this._suppressedAt = now();
      this._suppressUntil = this._suppressedAt + ms;
    }

    _click(event) {
      var target = event.target;
      if (!target || !target.closest || !this._items) return;
      if (target.closest(NO_ACTIVATION)) return;

      var item = target.closest(ITEM);
      var index = item ? this._items.indexOf(item) : -1;
      if (index < 0) return;

      // Someone selecting the text to copy it is not asking for the page to move.
      var selection = window.getSelection ? window.getSelection() : null;
      if (selection && !selection.isCollapsed && selection.anchorNode && item.contains(selection.anchorNode)) return;

      this._activate(index);
    }

    _activate(index) {
      var self = this;
      var item = this._items[index];
      var steps = Math.abs(index - Math.max(this._active, 0));
      var still = reducedMotion();

      // Several steps at once: they all finish together, inside a second.
      clearTimeout(this._restoreTimer);
      this.style.removeProperty('--_dur');
      if (steps > 1 && !still) {
        var pace = seconds(getComputedStyle(this).getPropertyValue('--_dur'), 0.5);
        this.style.setProperty('--_dur', (pace / steps).toFixed(3) + 's');
        this._restoreTimer = setTimeout(function () {
          self.style.removeProperty('--_dur');
        }, COMPRESS_MS);
      }

      this._started = true;
      this._setActiveUpTo(index);

      var rect = item.getBoundingClientRect();
      var height = window.innerHeight || document.documentElement.clientHeight;
      var delta = rect.top + rect.height / 2 - height / 2;
      if (Math.abs(delta) < 2) return;

      // The scroll this starts must not move the state back on its way there.
      var wait = Math.max(steps > 1 ? COMPRESS_MS : 0, clamp(Math.abs(delta) / 1.2, 200, 900)) + 50;
      // Where the browser says when a scroll has ended, that ends the wait; the timer is then only a cap.
      this._suppress(SCROLLEND && !still ? Math.max(wait, 1500) : wait);

      var top = Math.max(0, (window.pageYOffset || document.documentElement.scrollTop || 0) + delta);
      try {
        window.scrollTo({ top: top, behavior: still ? 'auto' : 'smooth' });
      } catch (error) {
        window.scrollTo(0, top);
      }
    }
  }

  customElements.define('pdp-timeline', PdpTimeline);
})();
